import React, { useState } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';

import {
  SliderMaskWrap,
  SlideLink,
  SlideImage,
  SliderArrow,
  SliderDots,
  SliderDot,
} from './styles';

interface MainSliderTypes {
  slideImgs?: any;
}

const Arrow: React.FC<{ left?: boolean }> = ({ left }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    {left ? (
      <path d="M16.67 0l2.83 2.829-9.339 9.175 9.339 9.167-2.83 2.829-12.17-11.996z" />
    ) : (
      <path d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z" />
    )}
  </svg>
);

const MainSlider: React.FC<MainSliderTypes> = ({ slideImgs }) => {
  const [loadedSlider, setLoadedSlider] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      initial: 0,
      loop: true,
      slides: {
        perView: 1,
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      created() {
        setLoadedSlider(true);
      },
    },
    [
      (slider) => {
        let timeout: ReturnType<typeof setTimeout>;
        let mouseOver = false;

        function clearNextTimeout() {
          clearTimeout(timeout);
        }

        function nextTimeout() {
          clearTimeout(timeout);
          if (mouseOver) return;
          timeout = setTimeout(() => {
            slider.next();
          }, 3000);
        }

        slider.on('created', () => {
          slider.container.addEventListener('mouseover', () => {
            mouseOver = true;
            clearNextTimeout();
          });
          slider.container.addEventListener('mouseout', () => {
            mouseOver = false;
            nextTimeout();
          });
          nextTimeout();
        });

        slider.on('dragStarted', clearNextTimeout);
        slider.on('animationEnded', nextTimeout);
        slider.on('updated', nextTimeout);

        // Sem isto, o autoplay segue trocando o banner de fundo para quem
        // pediu menos movimento.
        if (
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
          slider.on('created', clearNextTimeout);
          slider.on('animationEnded', clearNextTimeout);
          slider.on('updated', clearNextTimeout);
        }
      },
    ],
  );

  const contentSlideImgs: any[] = slideImgs && slideImgs.length > 0 ? slideImgs : [];

  if (contentSlideImgs.length === 0) return <></>;

  return (
    <SliderMaskWrap>
      <div ref={sliderRef} className="keen-slider">
        {contentSlideImgs.map((itemSlider: any, key: number) => {
          // O slide era uma <div> com background-image, que o navegador não
          // sabe priorizar nem adiar. Como <img>, o primeiro banner carrega
          // cedo e os demais só quando entram — o maior ganho de 3G do herói.
          const image = (
            <SlideImage
              src={itemSlider.url}
              alt=""
              loading={key === 0 ? 'eager' : 'lazy'}
              decoding="async"
              // @ts-ignore fetchpriority ainda não está nos tipos do React 18
              fetchpriority={key === 0 ? 'high' : 'low'}
              /* Banner era background-image, que falha em silêncio. Como
                 <img>, uma URL quebrada mostraria o ícone de imagem
                 quebrada em cima do herói. */
              onError={(event) => {
                event.currentTarget.style.display = 'none';
              }}
            />
          );

          return (
            <div key={key} className={`keen-slider__slide number-slide${key}`}>
              {itemSlider?.linkurl && itemSlider.linkurl !== ' ' ? (
                <SlideLink
                  href={itemSlider.linkurl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {image}
                </SlideLink>
              ) : (
                image
              )}
            </div>
          );
        })}
      </div>

      {loadedSlider && instanceRef.current && contentSlideImgs.length > 1 && (
        <>
          <SliderArrow
            type="button"
            $side="left"
            aria-label="Banner anterior"
            onClick={() => instanceRef.current?.prev()}
          >
            <Arrow left />
          </SliderArrow>

          <SliderArrow
            type="button"
            $side="right"
            aria-label="Próximo banner"
            onClick={() => instanceRef.current?.next()}
          >
            <Arrow />
          </SliderArrow>

          <SliderDots>
            {contentSlideImgs.map((_, key: number) => (
              <SliderDot
                key={key}
                type="button"
                $active={currentSlide === key}
                aria-label={`Ir para o banner ${key + 1}`}
                aria-current={currentSlide === key}
                onClick={() => instanceRef.current?.moveToIdx(key)}
              />
            ))}
          </SliderDots>
        </>
      )}
    </SliderMaskWrap>
  );
};

export default MainSlider;
